import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

/**
 * Confirm dialog for card deletes and archives — the single confirm used by
 * boards, concepts, collections, projects and the Recent grid, so all of them
 * look the same (#3792).
 *
 * `title` / `confirmLabel` default to the delete wording; boards and concepts
 * override them with archive wording, since both are soft deletes. `body` is
 * optional — archive flows name the item and say what happens to it; the plain
 * delete confirms (collections, projects) let the question stand alone.
 */
/**
 * Body copy for archive confirms whose surface has a restore path (boards'
 * archived list, the Recent grid's Undo). The concept list page deliberately
 * does NOT use it — no Undo or archived view there until #3794.
 *
 * Deliberately NOT "hidden from the project": archiving is a global soft
 * delete — `!b.archive` in useBoards, applyConceptArchiveFilter in useConcepts,
 * and `!s.archived` in both schedule listing functions all hide the item
 * everywhere, not just on the project it was filed to. "From the project" is
 * what "Remove from Project" does, and the two sit next to each other in the
 * same menu.
 */
export const archiveConfirmBody = (name: string) =>
  `“${name}” will be hidden. Archiving is reversible - you can restore it later!`;

export function DeleteComponentModal({
  open,
  setOpen,
  onDelete,
  title = 'Are you sure you want to delete this?',
  confirmLabel = 'Delete',
  body = null,
  confirmDisabled = false,
}) {
  const handleClose = () => {
    setOpen(false);
  };

  const handleDelete = () => {
    setOpen(false);
    onDelete();
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>{title}</DialogTitle>
      {body && (
        <DialogContent>
          <Typography sx={{ fontSize: '0.9rem', color: 'text.secondary' }}>
            {body}
          </Typography>
        </DialogContent>
      )}
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button color="error" onClick={handleDelete} disabled={confirmDisabled}>
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
